import { useRouter } from "next/navigation";
import { useMemo, type JSX } from "react";
import { useSelector } from "react-redux";
import { ChevronRightIcon, Divider } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import ExpandTitle from "@/components/atoms/expander/expand-title";
import { SINGLE_TRIP } from "@/constants";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { RootState } from "@/store/root-reducer";
import { formatDate, getNumberOfDates } from "@/utils/edit-quote.utils";

import editQuoteStyle from "./edit-quote.module.css";

interface Props {
  brand: "staysure" | "avanti";
  tripTitle?: string | null;
  typeLabel?: string | null;
  destinationLabel?: string | null;
  datesLabel?: string | null;
  cruiseLabel?: string | null;
  regionLabel?: string | null;
  policyStartLabel?: string | null;
  isOpen: boolean;
  onToggle: () => void;
}

function EditTrip(props: Readonly<Props>): JSX.Element {
  const {
    tripTitle = "View or edit trip",
    typeLabel = "trip type",
    destinationLabel = "travel Destination",
    regionLabel = "Region",
    datesLabel = "Travel Dates",
    cruiseLabel = "Cruise cover",
    policyStartLabel = "Policy start date",
    isOpen,
    onToggle,
    brand,
  } = props;
  const router = useRouter();
  const { current } = useWizard();
  const { attributes } = useSelector((state: RootState) => state.quote);
  const tripType = attributes.tripDetails?.policyType;
  const destination = attributes.tripDetails?.toLocationCountryDetails;
  const fromDate = attributes.tripDetails?.startDate;
  const toDate = attributes.tripDetails?.endDate;
  const isCruise = attributes.tripDetails?.goingOnCruise;
  const area = attributes.tripDetails?.area;
  const numOfDates = getNumberOfDates(fromDate, toDate);
  const fromDateFormat = formatDate(fromDate);
  const toDateFormat = formatDate(toDate);
  const { t } = useWizardTranslation();
  let trip;
  let destinationTag;
  let policyDateLable;
  let datesBtnLable;
  if (tripType === SINGLE_TRIP) {
    trip = t("single_trip", "Single trip");
    policyDateLable = datesLabel;
    destinationTag = destinationLabel;
    datesBtnLable = `${fromDateFormat} - ${toDateFormat} (${numOfDates} ${t(
      "days_translate",
      "days"
    )})`;
  } else {
    trip = t("multi_trip", "Multi trip");
    destinationTag = regionLabel;
    policyDateLable = policyStartLabel;
    datesBtnLable = fromDateFormat;
  }
  const annualTripBtn = useMemo(() => {
    if (!area) return "";

    const locationMap = {
      UK: t("uk_only", "UK only"),
      IE: t("ireland_only", "Ireland only"),
      ESP: t("spain_only", "Spain only"),
      EUROPE_LR: t("europe_with_exclusions", "Europe, with exclusions"),
      EUROPE_HR: t("europe", "Europe"),
      WORLD_LR: t("worldwide_with_exclusions", "Worldwide, with exclusions"),
      WORLD_HR: t("worldwide", "Worldwide"),
    };
    const location = area ? locationMap[area as keyof typeof locationMap] : "";
    return location;
  }, [area, t]);

  const handleTripType = () => {
    router.push(current.edges.coverTypeEdit ?? "");
  };
  const handleDestination = () => {
    router.push(current.edges.travelToDestinationEdit ?? "");
  };
  const handleDates = () => {
    router.push(current.edges.travelDatesEdit ?? "");
  };
  const handleCruise = () => {
    router.push(current.edges.cruiseEdit ?? "");
  };

  const renderIcon = () => {
    return (
      <div className="w-[24px] h-[24px] flex justify-end items-end">
        <ChevronRightIcon brand={brand} />
      </div>
    );
  };

  const renderBtn = (onClickMethod: () => void, label: string) => {
    return (
      <div className="w-[100%] flex">
        <LabelButtonWrapper
          label={label}
          className={`${editQuoteStyle.btnLink}`}
          onClick={onClickMethod}
        />
      </div>
    );
  };

  return (
    <div
      data-testid="edit-trip"
      className="md:first:pt-spacing-xs sm:first:pt-spacing-s"
    >
      <ExpandTitle
        title={tripTitle}
        setIsShow={onToggle}
        initShow={isOpen}
        id="edit-quote-trip-details-toggle"
        brand={brand}
      />
      {isOpen && (
        <div className="flex flex-col items-start gap-spacing-s">
          <div
            role="button"
            tabIndex={0}
            className="w-full cursor-pointer"
            onClick={handleTripType}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleTripType();
              }
            }}
            id="edit-quote-trip-type"
          >
            <div className="flex flex-row w-full justify-between items-center">
              <div className="flex flex-col items-start w-full">
                <span className="text-functional-subtle font-sub-section-header-font">
                  {typeLabel}
                </span>
                {renderBtn(handleTripType, trip)}
              </div>
              {renderIcon()}
            </div>
          </div>
          <Divider thickness={1} brand={brand} />
          <div
            role="button"
            tabIndex={0}
            className="w-full cursor-pointer"
            onClick={handleDestination}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleDestination();
              }
            }}
            id="edit-quote-destination"
          >
            <div className="flex flex-row w-full justify-between items-center">
              <div className="flex flex-col items-start">
                <span className="text-functional-subtle font-sub-section-header-font">
                  {destinationTag}
                </span>
                <div className="flex flex-col">
                  {tripType === SINGLE_TRIP ? (
                    destination?.map((country) => (
                      <div
                        key={country.attributes?.countryCode}
                        className="flex flex-row leading-[16px]"
                      >
                        <LabelButtonWrapper
                          label={country.attributes?.countryName ?? ""}
                          className={`${editQuoteStyle.btnLink}`}
                          onClick={handleDestination}
                        />
                      </div>
                    ))
                  ) : (
                    <LabelButtonWrapper
                      label={annualTripBtn}
                      className={`${editQuoteStyle.btnLink}`}
                      onClick={handleDestination}
                    />
                  )}
                </div>
              </div>
              {renderIcon()}
            </div>
          </div>
          <Divider thickness={1} brand={brand} />
          <div
            role="button"
            tabIndex={0}
            className="w-full cursor-pointer"
            onClick={handleDates}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleDates();
              }
            }}
            id="edit-quote-travel-dates"
          >
            <div className="flex flex-row w-full justify-between items-center">
              <div className="flex flex-col items-start">
                <span className="text-functional-subtle font-sub-section-header-font">
                  {policyDateLable}
                </span>
                {renderBtn(handleDates, datesBtnLable)}
              </div>
              {renderIcon()}
            </div>
          </div>
          <Divider thickness={1} brand={brand} />
          <div
            role="button"
            tabIndex={0}
            className="w-full cursor-pointer"
            onClick={handleCruise}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCruise();
              }
            }}
            id="edit-quote-travelling-cruise"
          >
            <div className="flex flex-row w-full justify-between items-center pb-spacing-m">
              <div className="flex flex-col items-start">
                <span className="text-functional-subtle font-sub-section-header-font">
                  {cruiseLabel}
                </span>
                {renderBtn(
                  handleCruise,
                  isCruise
                    ? t("included", "Included")
                    : t("not_included", "Not Included")
                )}
              </div>
              {renderIcon()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default EditTrip;
